/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Link, useTranslations } from "@i18n/next";
import type { Article } from "@packages/sdk";
import type { Color } from "@packages/ui";
import { CategoryLarge, Container, spacing, Stack, Text } from "@packages/ui";
import { SectionCarousel } from "@ui/sections";

const baseStyles: Interpolation = {
  paddingTop: spacing.m,
};

const titleStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
  justifyContent: "space-between",
};

export type LatestArticlesProps = {
  articles: Article[];
};

const ArticleCard = ({ article }: { article: Article }) => {
  const category = article.categories.find(
    (category) => category.slug !== "uncategorized",
  );

  return (
    <CategoryLarge
      description={category?.name}
      title={article.title}
      titleProps={{
        overflow: null,
      }}
      color={article.images.color_hex as Color}
      imgSrc={article.images}
      asChild
    >
      <Link href={article.link} target="_blank" />
    </CategoryLarge>
  );
};

export const LatestArticles = ({ articles }: LatestArticlesProps) => {
  const t = useTranslations();

  if (!articles?.length) return null;

  return (
    <Stack>
      <Container paddingX css={baseStyles}>
        <div css={titleStyles}>
          <Text as="h2" overflow="ellipsis" size="m" type="headline">
            {t("web_latest_articles_title")}
          </Text>
        </div>
      </Container>
      <SectionCarousel size="m">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </SectionCarousel>
    </Stack>
  );
};

LatestArticles.displayName = "LatestArticles";
